"use client";

import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Image from "../../components/common/Image";

const projects = [
  {
    title: "E-Commerce Platform",
    category: "Web Development",
    description: "A full-featured e-commerce solution with real-time inventory management.",
    image: "/images/portfolio/ecommerce.png",
    link: "/portfolio/ecommerce",
    technologies: ["React", "Node.js", "MongoDB", "Redis"]
  },
  {
    title: "Healthcare App",
    category: "Mobile Development",
    description: "Mobile healthcare platform for patient management and telemedicine.",
    image: "/images/portfolio/healthcare.png",
    link: "/portfolio/healthcare",
    technologies: ["Angular", "Node.js", "MongoDB", "WebRTC"]
  },
  {
    title: "Cloud Migration",
    category: "Cloud Solutions",
    description: "Enterprise-scale cloud migration and infrastructure optimization.",
    image: "/images/portfolio/cloud_migration.png",
    link: "/portfolio/cloud",
    technologies: ["AWS", "Docker", "Kubernetes"]
  },
//   {
//     title: "AI Analytics Dashboard",
//     category: "Data Analytics",
//     description: "Real-time analytics platform powered by machine learning.",
//     image: "/portfolio/analytics.webp",
//     link: "/portfolio/analytics",
//     technologies: ["Python", "TensorFlow", "D3.js"]
//   },
//   {
//     title: "Smart Home IoT",
//     category: "IoT Solutions",
//     description: "Connected home automation system with AI integration.",
//     image: "/portfolio/iot.webp",
//     link: "/portfolio/iot",
//     technologies: ["IoT", "MQTT", "Node.js"]
//   },
//   {
//     title: "Fintech Platform",
//     category: "Financial Technology",
//     description: "Secure financial transaction and cryptocurrency trading platform.",
//     image: "/portfolio/fintech.webp",
//     link: "/portfolio/fintech",
//     technologies: ["Blockchain", "Node.js", "React"]
//   }
];

export default function PortfolioPage() {
  return (
    <main className="min-h-screen bg-gradient-to-br from-white via-gray-50 to-white">
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <motion.div
          className="absolute inset-0 opacity-10"
          animate={{
            backgroundPosition: ["0% 0%", "100% 100%"],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            repeatType: "reverse",
          }}
          style={{
            backgroundImage: "url('/grid-pattern.svg')",
          }}
        />
        
        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-indigo-600 via-blue-600 to-purple-600 bg-clip-text text-transparent">
              Our Portfolio
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
              Explore our diverse range of successful projects and see how we've helped businesses transform their digital presence.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="py-12 sm:py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10">
            {projects.map((project, index) => (
              <motion.div
                key={project.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group relative bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl 
                  transition-all duration-500 hover:translate-y-[-5px]"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <Image
                    src={project.image}
                    alt={project.title}
                    width={800}
                    height={600}
                    className="w-full h-full object-cover transition-all duration-700 
                      group-hover:scale-105"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-transparent 
                  opacity-0 group-hover:opacity-100 transition-all duration-500">
                  <div className="absolute bottom-0 left-0 right-0 p-8">
                    <span className="inline-block px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md 
                      text-white text-sm mb-3 border border-white/10">
                      {project.category}
                    </span>
                    <h3 className="text-2xl font-bold text-white mb-3">
                      {project.title}
                    </h3>
                    <p className="text-gray-300 mb-4 line-clamp-2">
                      {project.description}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-6">
                      {project.technologies.map((tech) => (
                        <span key={tech} className="text-xs px-3 py-1 rounded-full bg-white/5 text-white/80">
                          {tech}
                        </span>
                      ))}
                    </div>
                    {/* <Link 
                      to={project.link} 
                      className="inline-flex items-center text-white group/link gap-2 
                        hover:gap-4 transition-all duration-300"
                    >
                      <span className="font-medium">View Project</span>
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                          d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </Link> */}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-indigo-600 to-purple-600 relative overflow-hidden">
        <motion.div
          className="absolute inset-0 opacity-10"
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 180, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear",
          }}
          style={{
            backgroundImage: "url('/grid-pattern.svg')",
          }}
        />
        <div className="container mx-auto px-4 relative">
          <div className="text-center text-white">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Ready to Start Your Project?</h2>
            <p className="text-lg sm:text-xl mb-8 text-white/80 max-w-2xl mx-auto">
              Let's work together to bring your vision to life with cutting-edge technology and innovative solutions.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-4 rounded-full bg-white text-indigo-600 
                font-semibold text-lg hover:shadow-lg transition-all duration-300
                hover:scale-105 group"
            >
              Get in Touch
              <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" 
                fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}